import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { IoCloudUploadOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import classes from "./ImageUploadModal.module.css";

const ImageUploadModal = ({
  isOpen,
  onClose,
  onUploadSuccess,
  selectedProduct,
  isBulk,
  type,
}) => {
  const { addToast } = useToasts();
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isProduct, setIsProduct] = useState(type);
  const dispatch = useDispatch();

  const [invalidFiles, setInvalidFiles] = useState([]);
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const navigate = useNavigate();

  const filenamePattern =
    /^[a-zA-Z0-9-_. ]+;(frontImage|frontFullImage|compare)\.(png|jpeg)$/;
  const validTags = ["frontImage", "frontFullImage", "compare"];

  const handleImageChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const validFiles = [];
    const invalidFilesList = [];

    newFiles.forEach((file) => {
      const reason = [];

      const isPng = file.type === "image/png" || file.type === "image/jpeg";
      const isValidName = filenamePattern.test(file.name);

      const nameParts = file.name.split(";");
      const prodSku = nameParts[0];
      const tagPart = nameParts[1]?.split(".")[0];

      const isValidTag = validTags.includes(tagPart);
      console.log(isValidTag);
      if (!isValidName) reason.push("Invalid filename format.");
      if (tagPart) {
        if (!isValidTag) reason.push(`Invalid tag: ${tagPart}.`);
      }
      if (!isPng) reason.push("Only PNG/JPEG allowed.");
      if (!isBulk && prodSku !== selectedProduct?.sSKU)
        reason.push("SKU doesn't match with product's SKU.");

      if (reason.length === 0) {
        validFiles.push(file);
      } else {
        invalidFilesList.push({ file, reason });
      }
    });

    setImageFiles((prev) => [...prev, ...validFiles]);
    setInvalidFiles(invalidFilesList);
  };

  const handleRemoveImage = (index) => {
    setImageFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveInvalidFile = (index) => {
    setInvalidFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (imageFiles.length === 0) {
      addToast("Please select at least one valid image to upload", {
        appearance: "error",
      });
      return;
    }

    const formData = new FormData();
    imageFiles.forEach((file) => {
      formData.append("productImages", file);
    });
    formData.append("sSKU", selectedProduct.sSKU);
    formData.append("bIsProduct", isProduct === "product" ? 1 : 0);

    setLoading(true);

    try {
      const response = await apiHandler({
        url: endpoint.UPLOAD_IMAGES,
        method: "POST",
        data: formData,
        authToken: authData,
        storeId,
        dispatch,
      });
      onClose();
      if (response.data.status === "success") {
        addToast("Images uploaded successfully", { appearance: "success" });
        onUploadSuccess();
        setImageFiles([]);
        setInvalidFiles([]);
      } else {
        addToast("Image upload failed", { appearance: "error" });
        setImageFiles([]);
        setInvalidFiles([]);
      }
    } catch (error) {
      addToast("Error uploading images", { appearance: "error" });
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (imageFiles.length === 0) {
      addToast("Please select at least one valid image to upload", {
        appearance: "error",
      });
      return;
    }

    const formData = new FormData();
    imageFiles.forEach((file) => {
      formData.append("productImages", file);
    });
    setLoading(true);
    formData.append("bIsProduct", isProduct === "product" ? 1 : 0);
    try {
      const response = await apiHandler({
        url: endpoint.UPLOAD_IMAGES,
        method: "POST",
        data: formData,
        authToken: authData,
        storeId,
        dispatch,
      });
      onClose();
      if (response.data.status === "success") {
        addToast("Images uploaded successfully", { appearance: "success" });
        onUploadSuccess();
        setImageFiles([]);
        setInvalidFiles([]);

        navigate("/bulkupload");
      } else {
        addToast("Image upload failed", { appearance: "error" });
      }
    } catch (error) {
      console.log(error);

      addToast("Error uploading images", { appearance: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={() => {
        onClose();
        setImageFiles([]);
      }}
      dialogClassName="modal-dialog-centered"
    >
      <Modal.Header closeButton>
        <Modal.Title>Upload Images</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="bg-yellow-100 text-yellow-800 p-2 rounded mb-4">
          <p className="text-sm font-semibold">
            Note: Name of the images should be in the following format:{" "}
            <span className="font-bold">productsku;tag</span>. Only{" "}
            <span className="font-bold">.png</span> are supported with size less
            equal to <span className="font-bold">500kb</span>
          </p>
          <p className="text-sm font-semibold">
            Predefined Tags:{" "}
            <span className="font-bold">frontImage,frontFullImage,compare</span>
          </p>
        </div>
        <div className={`${classes.filterbox} mb-4`}>
          <h1 className="mb-2">Select the type you want to upload images</h1>
          <select
            name="bIsProduct"
            value={isProduct}
            onChange={(e) => setIsProduct(e.target.value)}
            className="p-2 border rounded-md"
            disabled={!isBulk}
          >
            <option value="">Select Option</option>
            <option value={"product"}>Product</option>
            <option value={"variant"}>Variant</option>
          </select>
        </div>
        <div
          className={`border-2 bg-gray-200 border-dashed rounded-md p-6 transition duration-200 flex flex-col items-center
    ${
      isProduct
        ? "border-gray-400 hover:border-blue-500"
        : "border-gray-300 cursor-not-allowed opacity-50"
    }`}
        >
          <input
            type="file"
            multiple
            onChange={handleImageChange}
            className="hidden"
            id="file-upload"
            disabled={!isProduct}
            accept="image/jpeg, image/png, image/jpg"
          />
          <label
            htmlFor="file-upload"
            className={`flex flex-col items-center mb-4 
      ${
        isProduct
          ? "cursor-pointer text-blue-600 hover:text-blue-800"
          : "cursor-not-allowed text-gray-400"
      }`}
          >
            <IoCloudUploadOutline size={20} className="mb-2" />
            Click to Upload images
          </label>
        </div>
        <p className="text-gray-600 text-sm">
          Acceptable formats: JPG, JPEG, PNG.
        </p>
        <div className="mt-4 grid grid-cols-5 gap-4">
          {imageFiles.length > 0 &&
            imageFiles.map((file, index) => (
              <div key={index} className={`relative ${classes.bulkImageThumb}`}>
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="w-full h-32 object-cover rounded-md"
                />
                <button
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-700"
                >
                  &times;
                </button>
              </div>
            ))}
        </div>
        {invalidFiles.length > 0 && (
          <div className="mt-4">
            <h5 className="text-red-500">Invalid Files:</h5>
            <div className="grid grid-cols-5 gap-4 mt-2">
              {invalidFiles.map((item, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(item.file)}
                    alt={item.file.name}
                    className="w-full h-32 object-cover rounded-md"
                  />
                  <p className="text-red-600 text-sm">
                    {item.reason.join(", ")}
                  </p>
                  <button
                    onClick={() => handleRemoveInvalidFile(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-700"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isBulk ? (
          <>
            <Button
              variant="primary"
              onClick={handleBulkUpload}
              disabled={loading || invalidFiles.length > 0}
            >
              {loading ? "Uploading..." : "Upload"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={loading || invalidFiles.length > 0}
            >
              {loading ? "Uploading..." : "Upload"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImageUploadModal;
